import type { ReactNode } from "react";
import { useId } from "react";
import { Link } from "react-router-dom";
import {
	PRIORITY_CARD_COPILOT_TAB_QUERY_PARAM,
	PRIORITY_SUMMARY_CARD_DEFINITIONS,
} from "../constants/priority-cards.constants";
import { useInsightsSummaryQuery } from "../hooks/use-insights-summary-query";
import { MetricCard, type MetricCardTone } from "./metric-card";
import { PageHeader } from "./page-header";

type PriorityCardsProps = {
	title: string;
	subtitle: string;
	actions?: ReactNode;
};

const TONE_BY_KEY = {
	reminders: "warning",
	alerts: "error",
	opportunities: "neutral",
} as const satisfies Record<string, MetricCardTone>;

export const PriorityCards = ({
	title,
	subtitle,
	actions,
}: PriorityCardsProps) => {
	const summaryQuery = useInsightsSummaryQuery();
	const headingId = useId();

	return (
		<section aria-labelledby={headingId} className="space-y-4">
			<PageHeader
				actions={actions}
				subtitle={subtitle}
				title={<span id={headingId}>{title}</span>}
				titleAs="h2"
			/>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
				{PRIORITY_SUMMARY_CARD_DEFINITIONS.map((card) => {
					const Icon = card.icon;

					return (
						<Link
							className="block h-full rounded-xl outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
							key={card.key}
							to={`/copilot/summary?${new URLSearchParams({ [PRIORITY_CARD_COPILOT_TAB_QUERY_PARAM]: card.key }).toString()}`}
						>
							<MetricCard
								className="h-full"
								icon={<Icon className="size-full" />}
								isLoading={summaryQuery.isFetching}
								subtitle={card.subtitle}
								title={card.title}
								tone={TONE_BY_KEY[card.key]}
								value={summaryQuery.data[card.key]}
							/>
						</Link>
					);
				})}
			</div>
		</section>
	);
};
